/* Shopify-priced variants, serialized changes, and a durable journal for interrupted swaps. */
(() => {
  'use strict';
  const configNode = document.getElementById('souvenir-coaster-commerce-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent), pricing = window.SouvenirCoasterCartModel;
  if (!config.enabled || !config.productId) return; // Draft configuration must not affect other families.
  if (!pricing) throw Error('Coaster cart model did not load.');
  const journalKey = `souvenir-coaster-change:${config.productId}`;
  let catalogPromise;
  const money = cents => new Intl.NumberFormat('en-US', {style:'currency',currency:config.currency || 'USD'}).format(cents / 100);
  async function request(path, body) {
    const response = await fetch(`${config.root}${path}`, {method:body ? 'POST' : 'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json',...(body ? {'Content-Type':'application/json'} : {})},...(body ? {body:JSON.stringify(body)} : {})});
    const result = await response.json();
    if (!response.ok) { const error = Error(result.description || 'Your cart could not be updated.'); error.confirmedFailure = response.status >= 400 && response.status < 500; throw error; }
    return result;
  }
  function catalog() {
    if (!catalogPromise) catalogPromise = fetch(config.catalogUrl).then(r => {if (!r.ok) throw Error('Designs could not load.'); return r.json();}).then(d => {if (!Array.isArray(d.designs)) throw Error('Designs could not load.'); return d.designs;}).catch(e => {catalogPromise = null; throw e;});
    return catalogPromise;
  }
  function save(journal) { localStorage.setItem(journalKey, JSON.stringify(journal)); }
  function tokenLines(cart, token) { return cart.items.filter(item => item.properties?._coaster_transaction === token); }
  // Shopify changes line keys when discounts change. Persist the complete stable
  // identity, then use only freshly-read keys for removal.
  function identity(item) {
    return JSON.stringify([String(item.variant_id ?? item.id),
      Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b)),
      String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  }
  function sameAdded(actual, expected) {
    return actual.length === expected.length && expected.every(want => {
      const matches = actual.filter(item => identity(item) === identity(want));
      return matches.length === 1 && matches[0].quantity === want.quantity;
    });
  }
  function snapshot(item) { return {key:item.key,quantity:item.quantity,identity:identity(item)}; }
  function originalLines(cart, journal) {
    const available = cart.items.filter(item => item.properties?._coaster_transaction !== journal.token);
    const originals = [];
    for (const old of journal.old) {
      const matches = available.filter(item => old.identity ? identity(item) === old.identity : item.key === old.key);
      if (matches.length > 1 || (matches.length && (matches[0].quantity !== old.quantity || originals.includes(matches[0])))) throw Error('Your cart changed in another window. Please contact us to review the coaster selection before checkout.');
      if (matches.length) originals.push(matches[0]);
    }
    return originals;
  }
  async function rollback(journal, cart) {
    // Old key-only journals cannot identify a discount-rekeyed original safely.
    // Undo only this verified addition; never guess which old lines to delete.
    const added = tokenLines(cart, journal.token);
    const matches = added.every(item => journal.items.filter(want => identity(item) === identity(want) && item.quantity === want.quantity).length === 1);
    if (!matches || (added.length && journal.phase !== 'rollback' && (!sameAdded(added,journal.items) || added.reduce((sum,item) => sum + item.original_price * item.quantity,0) !== journal.expected))) throw Error('Please contact us to review the unfinished coaster update before checkout.');
    journal.phase = 'rollback'; save(journal);
    if (added.length) {
      try { await request('cart/update.js',{updates:Object.fromEntries(added.map(item => [item.key,0]))}); }
      catch (_) { /* Check before retrying an idempotent removal. */ }
    }
    cart = await request('cart.js');
    if (tokenLines(cart,journal.token).length) throw Error('Your coaster update is not finished. Choose “Check cart again” before continuing.');
    localStorage.removeItem(journalKey);
    return cart;
  }
  async function finish(journal) {
    let cart = await request('cart.js');
    if (!cart.items.length) {
      if (journal.items.length && journal.phase !== 'rollback') throw Error('We have not confirmed the last coaster update. Choose “Check cart again”; do not add it again.');
      localStorage.removeItem(journalKey);
      return cart;
    }
    if (journal.phase === 'rollback') return rollback(journal,cart);
    const added = tokenLines(cart, journal.token);
    if (journal.phase === 'adding' && journal.items.length && !added.length && originalLines(cart,journal).length === journal.old.length) {
      // A timed-out add can still commit later. Absence in one read is not rejection.
      throw Error('We have not confirmed the last coaster update. Choose “Check cart again”; do not add it again.');
    }
    if (!sameAdded(added, journal.items)) throw Error('We couldn’t confirm the last coaster update. Use “Check cart again” before continuing; do not add the items again.');
    if (added.reduce((sum, item) => sum + item.original_price * item.quantity, 0) !== journal.expected) throw Error('The saved coaster price changed. Please contact us before checking out.');
    const originals = originalLines(cart, journal);
    if (journal.old.some(old => !old.identity)) {
      if (originals.length !== journal.old.length) {
        if (journal.phase === 'adding') return rollback(journal,cart);
        throw Error('Please contact us to review the unfinished coaster update before checkout.');
      }
      journal.old = originals.map(snapshot); save(journal);
    }
    if (journal.phase === 'adding' && originals.length !== journal.old.length) throw Error('Your cart changed in another window. Please contact us before checkout.');
    journal.phase = 'removing'; save(journal);
    if (originals.length) {
      try { await request('cart/update.js', {updates:Object.fromEntries(originals.map(item => [item.key,0]))}); }
      catch (_) { /* A removal is idempotent. Read first; never repeat an add. */ }
    }
    cart = await request('cart.js');
    const finalAdded = tokenLines(cart,journal.token);
    if (finalAdded.reduce((sum,item) => sum + item.original_price * item.quantity,0) !== journal.expected) throw Error('The saved coaster price changed. Please contact us before checking out.');
    if (originalLines(cart, journal).length || !sameAdded(finalAdded,journal.items)) throw Error('Your coaster update is not finished. Choose “Check cart again” before continuing.');
    localStorage.removeItem(journalKey);
    return cart;
  }
  async function recover() {
    const saved = localStorage.getItem(journalKey);
    return saved ? finish(JSON.parse(saved)) : request('cart.js');
  }
  function ready(cart) {
    if (config.checkoutReady !== true || config.fulfillmentReady !== true || config.currency !== 'USD' || (cart.currency && cart.currency !== 'USD')) throw Error('Coaster checkout is being prepared. Your selection is saved.');
    for (const size of pricing.PACK_SIZES) {
      const variants = config.variants.filter(v => v.sku === `COAST-MIX-P${size}`);
      if (variants.length !== 1 || variants[0].price !== pricing.total(size) || !Number.isSafeInteger(variants[0].grams) || variants[0].grams <= 0) throw Error('Coaster prices and shipping are being prepared. Your selection is saved.');
    }
  }
  function revision(item) { return JSON.stringify([identity(item),item.quantity]); }
  function ownLines(cart) { return cart.items.filter(item => String(item.product_id) === String(config.productId)); }
  function selectionSignature(size,selection,designs) { return JSON.stringify([size,pricing.normalize(selection,designs)]); }
  function inspected(cart,designs) {
    const found = pricing.readCart(cart,designs,config);
    if (found.invalid.length) throw Error('A coaster pack has incomplete or changed details. Remove that pack before continuing.');
    return found;
  }
  async function transact(cart,old,requestPlan,designs) {
    const transaction = crypto.randomUUID();
    const planned = requestPlan ? pricing.planPack({...requestPlan,transaction},designs,config.variants) : {items:[],expected:0};
    const journal = {token:transaction,old:old.map(snapshot),items:planned.items,expected:planned.expected,phase:'adding'};
    save(journal); // No cart mutation unless recovery data can first be persisted.
    if (planned.items.length) {
      try { await request('cart/add.js',{items:planned.items}); }
      catch (error) {
        if (error.confirmedFailure) {
          const checked = await request('cart.js');
          if (!tokenLines(checked,transaction).length) { localStorage.removeItem(journalKey); throw error; }
        }
        // Do not repeat uncertain adds. Recover by the unique transaction token.
      }
    }
    return finish(journal);
  }
  async function locked(work,needsCatalog=true) {
    if (!navigator.locks) throw Error('Coaster shopping is unavailable in this browser right now. Please try a current browser or contact us.');
    return navigator.locks.request('souvenir-cart',async()=>{
      const cart = await recover(), designs = needsCatalog ? await catalog() : null;
      const result = await work(cart,designs);
      window.dispatchEvent(new CustomEvent('souvenir:cart-updated',{detail:result}));
      return result;
    });
  }
  const api = {
    config,money,catalog,total:pricing.total,
    hasLines:cart=>ownLines(cart).length>0,
    inspect:(cart,designs)=>pricing.readCart(cart,designs,config),
    get:()=>locked(async cart=>cart,false),
    validateCheckout:async cart=>{
      if (localStorage.getItem(journalKey)) throw Error('Your coaster update is unfinished. Choose “Check cart again” before checkout.');
      if (!ownLines(cart).length) return {lines:[],packs:[],invalid:[],expected:0,coasterCount:0,others:cart.items};
      ready(cart);
      return inspected(cart,await catalog());
    },
    addPack:pack=>locked(async(cart,designs)=>{
      ready(cart); const found=inspected(cart,designs);
      const packId=pack.packId;
      const existing=found.packs.find(p=>p.packId===packId);
      if (existing) {
        if (selectionSignature(existing.size,existing.selection,designs)!==selectionSignature(pack.size,pack.selection,designs)) throw Error('This pack was already added with a different selection. Review your cart.');
        return cart; // A stable client pack ID makes repeated submission idempotent after recovery.
      }
      return transact(cart,[],{size:pack.size,selection:pack.selection,packId},designs);
    }),
    editPack:(packId,pack,expectedRevision)=>locked(async(cart,designs)=>{
      ready(cart); const found=inspected(cart,designs), current=found.packs.find(p=>p.packId===packId);
      if (!current) throw Error('This coaster pack is no longer in your cart.');
      const line=found.lines.find(item=>item.properties?._coaster_pack===packId);
      if (!expectedRevision || revision(line)!==expectedRevision) throw Error('This coaster pack changed in another window. Reload it before editing.');
      if (current.quantity!==1) throw Error('This line contains repeated packs. Remove it and rebuild the packs you want to change.');
      if (selectionSignature(current.size,current.selection,designs)===selectionSignature(pack.size,pack.selection,designs)) return cart;
      return transact(cart,[line],{size:pack.size,selection:pack.selection,packId},designs);
    }),
    removePack:(packId,expectedRevision)=>locked(async(cart,designs)=>{
      const lines=ownLines(cart).filter(item=>item.properties?._coaster_pack===packId);
      if (!lines.length) return cart;
      if (lines.length!==1 || !expectedRevision || revision(lines[0])!==expectedRevision) throw Error('This coaster pack changed. Reload your cart before removing it.');
      return transact(cart,lines,null,designs);
    },false),
    removeLine:(key,expectedRevision)=>locked(async(cart,designs)=>{
      const line=ownLines(cart).find(item=>item.key===key);
      if (!line || !expectedRevision || revision(line)!==expectedRevision) throw Error('This coaster pack changed. Reload your cart before removing it.');
      return transact(cart,[line],null,designs);
    },false),
    identity,revision,newPackId:()=>crypto.randomUUID(),
    checkout:async()=>{
      const cart=await api.get(); await api.validateCheckout(cart);
      if (!cart.item_count) throw Error('Your cart is empty.');
      if (!window.SouvenirCheckout) throw Error('Checkout is loading. Please try again.');
      return window.SouvenirCheckout();
    }
  };
  window.SouvenirCoasters=api;
  window.dispatchEvent(new Event('souvenir:commerce-ready'));
})();
